import React from 'react';
import SEO from '../components/SEO';

const TERMS_SCHEMA = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'WebPage',
      '@id': 'https://phennx.io/terms-and-conditions#webpage',
      url: 'https://phennx.io/terms-and-conditions',
      name: 'Terms and Conditions',
      description: 'Review the terms and conditions for using the Phennx website.',
      isPartOf: { '@id': 'https://phennx.io/#website' },
      about: { '@id': 'https://phennx.io/#organization' },
    },
    {
      '@type': 'BreadcrumbList',
      '@id': 'https://phennx.io/terms-and-conditions#breadcrumbs',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: 'Home',
          item: 'https://phennx.io/',
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: 'Terms and Conditions',
          item: 'https://phennx.io/terms-and-conditions',
        },
      ],
    },
  ],
};

export default function TermsConditionsPage() {
  return (
    <>
      <SEO
        title="Terms and Conditions"
        description="Review the terms and conditions for using the Phennx website."
        path="/terms-and-conditions"
        image="/logo-full-transparent.png"
        type="article"
        schema={TERMS_SCHEMA}
      />
      <article className="legal-page" aria-label="Terms and Conditions">
        <header className="legal-page__hero">
          <div className="section-inner">
            <div className="legal-page__eyebrow">Legal</div>
            <h1 className="legal-page__title">Terms &amp; Conditions</h1>
            <p className="legal-page__lede">
              These Terms of Use are a legal agreement between you and Phennx governing your access to and use of the
              public portions of the Phennx website.
            </p>
            <p className="legal-page__updated">Last updated: November 1, 2024</p>
          </div>
        </header>

        <section className="legal-page__content">
          <div className="section-inner">
            <div className="legal-page__section">
              <h2>Acceptance of terms</h2>
              <p>
                These Terms of Use apply to your access to and use of the Site and do not alter the terms of any other
                agreement you may have with Phennx. By using or accessing the Site, you agree to these Terms of Use,
                all applicable laws and regulations, and our Website Privacy Policy.
              </p>
              <p>
                If you do not agree to these Terms of Use, you must not use or access the Site.
              </p>
            </div>

            <div className="legal-page__section">
              <h2>Eligibility</h2>
              <p>
                The Site is offered and available to users in the United States who are 13 years of age or older. You
                may not use or access the Site if you do not agree to these Terms of Use, are not at least 13 years of
                age, or are accessing or using the Site from outside of the United States.
              </p>
            </div>

            <div className="legal-page__section">
              <h2>Site ownership and intellectual property</h2>
              <p>
                The Site and its content, features, and functionality are protected by applicable intellectual
                property laws. The Site and Content are the property of Phennx and/or its licensors, and all rights,
                title, and interest remain with Phennx or such licensors.
              </p>
              <p>
                These Terms of Use permit you to use the Site for your personal, non-commercial use only. You do not
                acquire any ownership rights other than the limited rights expressly granted in these Terms of Use.
              </p>
            </div>

            <div className="legal-page__section">
              <h2>Feedback</h2>
              <p>
                If you provide feedback, suggestions, or ideas to Phennx, you grant us a nonexclusive, royalty-free,
                worldwide, perpetual, irrevocable, transferable, and fully sublicensable right to use that feedback
                for any purpose without compensation to you.
              </p>
            </div>

            <div className="legal-page__section">
              <h2>Prohibited uses</h2>
              <p>You may use the Site only as expressly permitted and only for lawful purposes. You agree not to:</p>
              <ul className="legal-page__list">
                <li>Use the Site in any way that violates any applicable law or regulation.</li>
                <li>Transmit or send unsolicited commercial communications.</li>
                <li>Gain unauthorized access to, interfere with, damage, or disrupt the Site or its infrastructure.</li>
                <li>Access the Site through any robot, spider, or other automated means.</li>
                <li>Screen scrape, monitor, mine, copy, or mirror the Site or Content.</li>
                <li>Conduct systematic or automated data collection without our express written consent.</li>
                <li>Introduce spyware, viruses, Trojan horses, worms, or other malicious code.</li>
                <li>Modify, reverse engineer, decompile, or create derivative works from the Site or Content.</li>
                <li>Reproduce, redistribute, republish, duplicate, copy, display, sell, or resell any Content.</li>
                <li>Remove or alter trademarks, copyrights, or other proprietary notices.</li>
                <li>Impersonate Phennx, our employees, or any other person or entity.</li>
                <li>Forge headers or manipulate identifiers to disguise the origin of content transmitted via the Site.</li>
              </ul>
            </div>

            <div className="legal-page__section">
              <h2>Information about you and your visits to the Site</h2>
              <p>
                Personal information we collect via the Site is subject to our Website Privacy Policy. By using the
                Site, you consent to all actions taken with respect to your information in compliance with our Privacy
                Policy.
              </p>
            </div>

            <div className="legal-page__section">
              <h2>Availability; revisions; reliance on information posted</h2>
              <p>
                The Content is provided for general information purposes only. We reserve the right to modify or
                discontinue the Site at any time without notice and do not guarantee continuous or secure access.
              </p>
              <p>
                We do not warrant the accuracy, completeness, or usefulness of the Site or Content. Any reliance you
                place on such information is strictly at your own risk.
              </p>
            </div>

            <div className="legal-page__section">
              <h2>Linking to the Site and social media features</h2>
              <p>
                You may link to the Site’s homepage in a way that is fair and legal, but you must not imply any
                association, approval, or endorsement by Phennx without our prior written consent.
              </p>
              <p>
                The Site may include social media features. You may use these features only as provided by Phennx and
                only with respect to the content they are displayed with. We reserve the right to withdraw linking
                permission or disable features at any time without notice.
              </p>
            </div>

            <div className="legal-page__section">
              <h2>Links from the Site; third parties</h2>
              <p>
                The Site may contain links to third-party websites for your convenience. We do not control those sites
                and are not responsible for their content or any loss or damage that may arise from your use of them.
              </p>
              <p>
                The Site may also include content provided by third parties. Any opinions expressed in such materials
                are solely those of the person or entity providing them.
              </p>
            </div>

            <div className="legal-page__section">
              <h2>No warranties</h2>
              <p>
                The Site and Content are provided on an “as is” and “as available” basis. To the fullest extent
                permitted by law, Phennx disclaims all warranties of any kind, whether express or implied.
              </p>
              <p>
                We do not guarantee that files available for downloading will be free of viruses or other destructive
                code. You are responsible for implementing safeguards to protect your systems and data.
              </p>
            </div>

            <div className="legal-page__section">
              <h2>Limitation of liability</h2>
              <p>
                To the fullest extent permitted by law, Phennx will not be liable for any indirect, incidental,
                consequential, special, exemplary, or punitive damages arising out of or related to your use of the
                Site or Content.
              </p>
              <p>
                In no event will Phennx’s aggregate liability arising out of or related to the Site exceed $50.
              </p>
            </div>

            <div className="legal-page__section">
              <h2>Governing law and venue</h2>
              <p>
                All matters relating to the Site and these Terms of Use will be governed by and construed in accordance
                with the laws of Florida, without giving effect to any choice or conflict of law provision.
              </p>
              <p>
                The courts located in Florida will have exclusive jurisdiction over any disputes arising out of or
                related to these Terms of Use.
              </p>
            </div>

            <div className="legal-page__section">
              <h2>Waiver and severability</h2>
              <p>
                No waiver by Phennx of any term or condition will be deemed a further or continuing waiver of such term
                or condition. If any provision of these Terms of Use is held invalid or unenforceable, the remaining
                provisions will remain in full force and effect.
              </p>
            </div>

            <div className="legal-page__section">
              <h2>Indemnification</h2>
              <p>
                You agree to indemnify, defend, and hold harmless Phennx and its affiliates, officers, directors,
                agents, and employees from and against any claims, liabilities, damages, judgments, awards, losses,
                costs, or expenses arising out of your breach of these Terms of Use or your use of the Site.
              </p>
            </div>

            <div className="legal-page__section">
              <h2>Changes to these Terms of Use</h2>
              <p>
                We may modify these Terms of Use at any time in our sole discretion by updating this posting. Your
                continued use of the Site constitutes acceptance of any changes.
              </p>
            </div>

            <div className="legal-page__section">
              <h2>Entire agreement</h2>
              <p>
                These Terms of Use, together with any documents expressly referred to in them, including our Website
                Privacy Policy, constitute the entire agreement between you and Phennx regarding the Site.
              </p>
            </div>

            <div className="legal-page__section">
              <h2>Termination</h2>
              <p>
                We reserve the right to terminate your access to the Site, in whole or in part, at any time without
                notice in our sole discretion.
              </p>
            </div>

            <div className="legal-page__section">
              <h2>Contact information</h2>
              <p>All questions about these Terms of Use should be directed to info@phennx.io.</p>
            </div>
          </div>
        </section>
      </article>
    </>
  );
}
